import { T, LogoMark, Wordmark } from '../theme';
import GlobalFooterBar from './GlobalFooterBar';

const EFFECTIVE_DATE = 'June 9, 2026';

function PrivacyPage({ onClose, standalone = false }) {
  const wrap = standalone ? {
    minHeight: '100vh',
    background: T.appBg,
    fontFamily: T.fontBody,
    paddingBottom: 36,
  } : {
    position: onClose ? 'fixed' : 'relative',
    inset: onClose ? 0 : undefined,
    background: onClose ? 'rgba(0,0,0,0.6)' : 'transparent',
    zIndex: onClose ? 2000 : undefined,
    display: 'flex',
    alignItems: onClose ? 'center' : undefined,
    justifyContent: onClose ? 'center' : undefined,
    backdropFilter: onClose ? 'blur(4px)' : undefined,
  };

  const card = {
    background: '#fff',
    borderRadius: standalone ? 0 : (onClose ? 20 : 0),
    width: '100%',
    maxWidth: standalone ? undefined : (onClose ? 640 : undefined),
    maxHeight: onClose ? '88vh' : undefined,
    overflowY: onClose ? 'auto' : undefined,
    padding: standalone ? '32px 24px' : '32px 28px',
    boxSizing: 'border-box',
    fontFamily: T.fontBody,
    margin: standalone ? '0 auto' : undefined,
  };

  const h2 = { fontSize: 15, fontWeight: 800, color: T.navy, margin: '24px 0 8px' };
  const p  = { fontSize: 14, color: '#374151', lineHeight: 1.7, margin: '0 0 10px' };
  const li = { fontSize: 14, color: '#374151', lineHeight: 1.7, marginBottom: 4 };

  const content = (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <LogoMark size={22} />
            <Wordmark size={16} lightColor='#080808'/>
          </div>

          <h1 style={{ fontSize: 22, fontWeight: 800, color: T.navy, margin: 0 }}>Privacy Policy</h1>
          <p style={{ ...p, margin: '4px 0 0', color: '#6B7280', fontSize: 13 }}>Effective {EFFECTIVE_DATE}</p>
        </div>
        {onClose && (
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#9CA3AF', padding: 4 }}>×</button>
        )}
      </div>

      <p style={p}>
        MySpend.info ("we", "us", "our") is committed to protecting your privacy. This policy explains what information we collect, how we use it, and your rights regarding that information.
      </p>

      <h2 style={h2}>1. Information We Collect</h2>
      <p style={p}><strong>Account information:</strong> When you create an account, we collect your email address and a securely hashed password (managed by Firebase Authentication).</p>
      <p style={p}><strong>Expense data:</strong> Expense records, categories, amounts, dates, vendors, notes, and any other information you enter into the app. This data is stored in your account and is used solely to provide the service to you.</p>
      <p style={p}><strong>Receipt images:</strong> Photos or PDFs you upload for AI scanning. Images are stored in Firebase Storage under your account and processed by AI models to extract expense data.</p>
      <p style={p}><strong>Usage data:</strong> We collect limited usage metrics (e.g. scan count, feature usage) to enforce plan limits and improve the service. We do not sell this data.</p>
      <p style={p}><strong>Billing information:</strong> If you subscribe to a paid plan, payment is processed by Stripe. We do not store your full card number; Stripe provides us with a customer ID and subscription status only.</p>
      <p style={p}><strong>Bank connection data (Plaid):</strong> If you use the Plaid integration to connect financial accounts, your Plaid access token is either encrypted and stored locally in your browser (local mode) or encrypted and stored in Firestore under your account (cloud mode). In both cases, every sync request is proxied through a MySpend server function — the token is used in memory and never logged or written to disk by our servers.</p>

      <h2 style={h2}>2. How We Use Your Information</h2>
      <ul style={{ paddingLeft: 20, margin: '0 0 10px' }}>
        <li style={li}>To provide and maintain the expense tracking service</li>
        <li style={li}>To process AI receipt scanning requests</li>
        <li style={li}>To enforce usage limits and manage subscriptions</li>
        <li style={li}>To send transactional emails (account confirmation, password reset, billing receipts)</li>
        <li style={li}>To improve the service based on aggregated, anonymized usage patterns</li>
      </ul>
      <p style={p}>We do not sell your personal data to third parties. We do not use your expense data to train AI models without your explicit consent.</p>

      <h2 style={h2}>3. Third-Party Services</h2>
      <p style={p}>We use the following third-party services, each governed by their own privacy policies:</p>
      <ul style={{ paddingLeft: 20, margin: '0 0 10px' }}>
        <li style={li}><strong>Google Firebase</strong> — authentication, database (Firestore), file storage, and server functions. <a href="https://firebase.google.com/support/privacy" style={{ color: T.tealDeep }}>Firebase Privacy</a></li>
        <li style={li}><strong>Google Gemini</strong> — AI model used for receipt scanning. Images sent for scanning are subject to Google's data use policies.</li>
        <li style={li}><strong>Stripe</strong> — payment processing for paid plans. <a href="https://stripe.com/privacy" style={{ color: T.tealDeep }}>Stripe Privacy</a></li>
        <li style={li}><strong>Plaid</strong> — optional bank account connectivity. <a href="https://plaid.com/legal/privacy-statement/" style={{ color: T.tealDeep }}>Plaid Privacy</a></li>
      </ul>

      <h2 style={h2}>4. Data Retention & Deletion</h2>
      <p style={p}>Your data is retained for as long as your account is active. You can export your expense data at any time from within the app. You can delete individual expenses and reports from the app. To request full account deletion (including all stored data), contact us at <span style={{ color: T.tealDeep, fontWeight: 600 }}>support@myspend.info</span> — we will process the request within 30 days.</p>

      <h2 style={h2}>5. Security</h2>
      <p style={p}>We use industry-standard security measures including encrypted data transmission (HTTPS), Firebase security rules that restrict data access to the owning account, and AES-GCM encryption for locally stored Plaid tokens. No system is perfectly secure; we encourage you to use a strong, unique password for your MySpend.info account.</p>

      <h2 style={h2}>6. Children's Privacy</h2>
      <p style={p}>MySpend.info is not directed at children under 13. We do not knowingly collect personal information from children under 13. If we become aware that we have done so, we will delete that information promptly.</p>

      <h2 style={h2}>7. Changes to This Policy</h2>
      <p style={p}>We may update this Privacy Policy from time to time. We will notify you of material changes via email or an in-app notice. Continued use of MySpend.info after changes take effect constitutes acceptance of the updated policy.</p>

      <h2 style={h2}>8. Contact</h2>
      <p style={p}>Questions or requests regarding your privacy? Contact us at <span style={{ color: T.tealDeep, fontWeight: 600 }}>support@myspend.info</span>.</p>

      {onClose && (
        <button
          onClick={onClose}
          style={{
            marginTop: 24, width: '100%', padding: '13px', borderRadius: 12, border: 'none',
            background: T.gradTeal, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
          }}
        >
          Close
        </button>
      )}
    </div>
  );

  if (standalone) {
    return (
      <div style={wrap}>
        <div style={{ background: T.gradNavy, padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <LogoMark size={20} />
            <Wordmark size={14} />
          </div>
          <a href="/" style={{ color: '#fff', fontSize: 13, fontWeight: 600, textDecoration: 'none', opacity: 0.85 }}>← Open App</a>
        </div>
        <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 0 84px' }}>
          {content}
        </div>
        <GlobalFooterBar />
      </div>
    );
  }

  return (
    <div style={wrap} onClick={onClose ? (e => { if (e.target === e.currentTarget) onClose(); }) : undefined}>
      {content}
    </div>
  );
}

export default PrivacyPage;
